import { Fragment, useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Users,
  Plus,
  Crown,
  FileClock,
  FileWarning,
  ChevronRight,
} from '@/components/icons'
import { StatCard } from './ui/stat-card'
import { hapticLight } from '@/lib/haptics'
import { AlertBadge } from './ui/alert-badge'
import { CrewDrawer } from './CrewDrawer'
import { useCrewGetAll } from '../lib/hooks'
import { useIsAdmin } from '../hooks/useIsCompanyAdmin'
import { toast } from 'sonner'
import { ProfileAvatar } from './ProfileAvatar'
import { MenuItem } from './ui/menu-item'
import { SearchInput } from './ui/search-input'
import { getInitials, formatCrewRole } from '@/lib/utils'
import { Button } from './ui/button'
import { TopBarBackIconButton } from './topbar'
import { AppLayout } from './AppLayout'
import { EmptyState } from './EmptyState'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'
import { Link } from '@tanstack/react-router'

interface CrewDirectoryProps {
  initialSearch?: string
  basePath?: string
  initialOpenDrawer?: boolean
}

export function CrewDirectory({ initialSearch, basePath = '/operations/crew-directory', initialOpenDrawer }: CrewDirectoryProps) {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { search?: string; tab?: 'active' | 'deleted' }
  const { isAdmin, isLoaded: isAdminLoaded } = useIsAdmin()
  const [searchQuery, setSearchQuery] = useState(initialSearch ?? '')
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(initialOpenDrawer ?? false)

  const activeTab: 'active' | 'deleted' = search.tab === 'deleted' ? 'deleted' : 'active'

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate({
        to: basePath,
        search: {
          ...search,
          search: searchQuery || undefined,
        } as any,
        replace: true,
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery, navigate])

  const handleTabChange = (value: string) => {
    navigate({
      to: basePath,
      search: {
        ...search,
        tab: value === 'deleted' ? 'deleted' : undefined,
      } as any,
      replace: true,
    })
  }

  const { data: allCrewMembers = [], isPending: loading } = useCrewGetAll({
    includeDeleted: true,
  })

  const visibleCrewMembers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const list = q
      ? allCrewMembers.filter((c) => {
          // Admins are searchable by "admin" even though the row shows their
          // crew role (the crown badge marks admin status).
          const role = formatCrewRole(c.crewRole)
          const adminTag = c.isAdmin ? 'Admin' : ''
          return [c.name, c.firstName, c.lastName, role, adminTag].some((v) =>
            v?.toLowerCase().includes(q)
          )
        })
      : allCrewMembers
    return [...list].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    )
  }, [allCrewMembers, searchQuery])

  const activeCrewMembers = visibleCrewMembers.filter((c) => !c.isDeleted)
  const deletedCrewMembers = visibleCrewMembers.filter((c) => c.isDeleted)

  // Stat cards ignore the search — they always reflect the full list.
  const unfilteredActiveCrew = allCrewMembers.filter((c) => !c.isDeleted)

  const renderCrewList = (list: typeof allCrewMembers) => (
    <Card className="p-2">
      <div className="flex flex-col gap-2">
        {list.map((crew, index) => (
          <Fragment key={crew.id}>
            {index > 0 && <hr className="border-border-L1" />}
            <MenuItem
              size="large"
              leftIcon={
                <div className="relative">
                  <ProfileAvatar
                    className="size-8"
                    photoS3Key={crew.photoS3Key}
                    avatarColor={crew.avatarColor}
                    initials={getInitials(crew.firstName, crew.lastName)}
                    fallbackClassName="text-xs text-background"
                  />
                  {crew.isAdmin && (
                    <div className="absolute -top-0.5 -right-0.5 size-4 bg-background-brand-bold rounded-full flex items-center justify-center p-0.5">
                      <Crown className="size-full text-content-primary-inverse" />
                    </div>
                  )}
                </div>
              }
              secondaryText={formatCrewRole(crew.crewRole)}
              rightIcon={
                <span className="flex items-center gap-2">
                  {!crew.isDeleted && crew.documentStatus === 'expiring' && (
                    <AlertBadge variant="warning" />
                  )}
                  {!crew.isDeleted && crew.documentStatus === 'expired' && (
                    <AlertBadge variant="error" />
                  )}
                  <ChevronRight className="size-5 text-content-primary-subtle" />
                </span>
              }
              onClick={() => navigate({ to: `${basePath}/${crew.id}` })}
            >
              <span className={crew.isDeleted ? 'text-content-primary-subtle' : ''}>
                {crew.name || 'Unknown'}
              </span>
            </MenuItem>
          </Fragment>
        ))}
      </div>
    </Card>
  )

  const renderCrewListSkeleton = () => (
    <Card className="p-2">
      <div className="flex flex-col gap-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index}>
            <div className="flex items-center gap-3 px-3 py-2">
              <Skeleton className="size-8 rounded-full shrink-0" />
              <div className="flex flex-col gap-2 flex-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            {index < 5 && <div className="h-px bg-border-L1 mx-3" />}
          </div>
        ))}
      </div>
    </Card>
  )

  const handleCrewSuccess = () => {
    setIsAddDrawerOpen(false)
    toast.success('Crew member saved successfully')
  }

  const isDesktop = useIsDesktop()

  const body = (
    <div className="flex flex-col gap-4 pt-4">
        {/* Sticky search band; the tab header pins right below it (top-11 = input height) */}
        <div className="sticky top-0 z-20 bg-surface-brand-L0">
          <SearchInput
            placeholder="Search crew by name, role"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
          />
        </div>

        {/* KPI Row */}
        {loading ? (
          <div className="flex w-full gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[88px] flex-1 rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="flex w-full gap-3">
            <StatCard
              variant="default"
              className="flex-1 min-w-0"
              label="Total"
              value={unfilteredActiveCrew.length}
              icon={<Users />}
              caption="Active crew"
            />
            <StatCard
              variant="default"
              className="flex-1 min-w-0"
              label="Expiring"
              value={
                unfilteredActiveCrew.filter((c) => c.documentStatus === 'expiring')
                  .length
              }
              icon={<FileClock />}
              caption="Next 30 days"
            />
            <StatCard
              variant="default"
              className="flex-1 min-w-0"
              label="Expired"
              value={
                unfilteredActiveCrew.filter((c) => c.documentStatus === 'expired')
                  .length
              }
              icon={<FileWarning />}
              caption="Need renewal"
            />
          </div>
        )}

        {/* Crew Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          {/* Sticky tab header: opaque, full-width band so the list scrolls beneath it */}
          <div className="sticky top-11 z-10 bg-surface-brand-L0 pb-2">
            <TabsList className="w-full">
              <TabsTrigger value="active">
                Active {activeCrewMembers.length}
              </TabsTrigger>
              <TabsTrigger value="deleted">
                Deleted {deletedCrewMembers.length}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="active" className="flex flex-col">
            {loading ? (
              renderCrewListSkeleton()
            ) : activeCrewMembers.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No crew members yet"
                description="Add your first crew member to start using Pronteer."
                addButtonText={isAdmin ? 'Add Crew Member' : undefined}
                onAdd={isAdmin ? () => setIsAddDrawerOpen(true) : undefined}
              />
            ) : (
              renderCrewList(activeCrewMembers)
            )}
          </TabsContent>

          <TabsContent value="deleted" className="flex flex-col">
            {loading ? (
              renderCrewListSkeleton()
            ) : deletedCrewMembers.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No deleted crew"
                description="Crew members you remove will appear here."
              />
            ) : (
              renderCrewList(deletedCrewMembers)
            )}
          </TabsContent>
        </Tabs>

        {/* Add Crew Drawer */}
        <CrewDrawer
          isOpen={isAddDrawerOpen}
          onClose={() => setIsAddDrawerOpen(false)}
        />
      </div>
  )

  const breadcrumb = (
    <span>
      <Link to="/operations" className="hover:underline">
        Operations
      </Link>
      <span className="text-content-primary-subtle"> / Crew</span>
    </span>
  )

  const addAction =
    isAdminLoaded && isAdmin ? (
      <Button
        roundness="round"
        variant="brand"
        size="sm"
        className="gap-2"
        onClick={() => {
          hapticLight()
          setIsAddDrawerOpen(true)
        }}
      >
        Add Crew
        <Plus className="size-4 text-content-primary-white" />
      </Button>
    ) : undefined

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Crew Directory"
          breadcrumb={breadcrumb}
          action={addAction}
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          title="Crew Directory"
          icon={
            isAdminLoaded && isAdmin ? (
              <Button roundness='round' variant='brand' size='sm' className="size-8 p-0">
                <Plus className="size-4 text-content-primary-white" />
              </Button>
            ) : undefined
          }
          onIconClick={
            isAdminLoaded && isAdmin
              ? () => {
                  hapticLight()
                  setIsAddDrawerOpen(true)
                }
              : undefined
          }
        />
      }
      className="pb-4"
    >
      {body}
    </AppLayout>
  )
}
